import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getCodes } from 'country-list';
import styles from './style.module.scss';
import InactiveIcon from '@components-clean/core/icons/inactive-icon/inactive-icon';
import { InfoField } from '@components-clean/core/info-field';
import Tooltip from '@components-clean/core/tooltip';
import { RiskMonitoringTab } from '@pages-clean/risk-assessment/constants';
import { ExtendedRiskObservationFields } from '@pages-clean/risk-assessment/risk-log/constants';
import { convertBooleanToYesNo, stringDateWithoutTimeFromUtc } from '@utils/convertor';

const ObservationArea = ({
    selectedObservation,
    parent,
}: {
    selectedObservation: ExtendedRiskObservationFields | undefined;
    parent: RiskMonitoringTab;
}) => {
    const { t } = useTranslation('ram', {
        keyPrefix:
            parent === RiskMonitoringTab.ManageObservations
                ? 'riskMonitoring.manageObservations'
                : 'riskMonitoring.manageActions',
    });
    const { t: tcommon } = useTranslation('common');
    const countryCodes = useMemo(() => getCodes(), []);

    if (!selectedObservation)
        return (
            <div className="k-h-full">
                <InfoField type="Info" message={t('info.noParentRecordSelected')} layout="center" />
            </div>
        );

    const vendorList = selectedObservation?.vendors?.map((a) => a?.name).join(', ') ?? '';
    const countryList = selectedObservation?.countries?.map((a) => a?.name).join(', ') ?? '';
    const vendorInactive = selectedObservation?.vendors && selectedObservation.vendors?.some((v) => v && !v.active);
    const countryInactive =
        selectedObservation?.countries &&
        selectedObservation.countries?.some((c) => !c?.code || !countryCodes.includes(c.code));

    return (
        <div style={{ padding: '12px', height: '100%' }}>
            <div className={styles.parent}>
                <div className={styles.container}>
                    <div>
                        {selectedObservation.deleted && (
                            <div>
                                <span className="k-font-medium">{t('tabs.observation.isDeleted')}</span>
                                <span>{convertBooleanToYesNo(selectedObservation?.deleted ?? false)}</span>
                            </div>
                        )}
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.status')}</span>
                            <span>{selectedObservation?.status}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.numActions')}</span>
                            <span>{selectedObservation?.numActions}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.riskStatement')}</span>
                            <span>{selectedObservation?.riskStatement}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.observation')}</span>
                            <span>{selectedObservation?.description}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.observationDate')}</span>
                            <span>
                                {stringDateWithoutTimeFromUtc(selectedObservation.observationDate, 'DD-MMM-YYYY')}
                            </span>
                        </div>
                        <div style={{ maxWidth: '600px' }}>
                            <span className="k-font-medium">{t('tabs.observation.vendors')}</span>
                            <span className={styles.inactiveFieldType}>
                                {vendorList?.length > 1000 ? (
                                    <Tooltip
                                        anchorElement="target"
                                        position="top"
                                        parentTitle
                                        style={{ maxWidth: '300px' }}
                                    >
                                        <span title={vendorList}>{`${vendorList?.slice(0, 47)}...`}</span>
                                    </Tooltip>
                                ) : (
                                    <span>{vendorList}</span>
                                )}
                                {vendorInactive && (
                                    <InactiveIcon
                                        toolTipText={tcommon('common:multipleRecordsInactiveIcon')}
                                        dataTestId="inactive-icon-vendors"
                                        tooltipPosition="bottom"
                                    />
                                )}
                            </span>
                        </div>
                        <div style={{ maxWidth: '600px' }}>
                            <span className="k-font-medium">{t('tabs.observation.countries')}</span>
                            <span className={styles.inactiveFieldType}>
                                {countryList?.length > 1000 ? (
                                    <Tooltip
                                        anchorElement="target"
                                        position="top"
                                        parentTitle
                                        style={{ maxWidth: '300px' }}
                                    >
                                        <span title={countryList}>{`${countryList?.slice(0, 47)}...`}</span>
                                    </Tooltip>
                                ) : (
                                    <span>{countryList}</span>
                                )}
                                {countryInactive && (
                                    <InactiveIcon
                                        toolTipText={tcommon('common:multipleRecordsInactiveIcon')}
                                        dataTestId="inactive-icon-countries"
                                        tooltipPosition="bottom"
                                    />
                                )}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.classification')}</span>
                            <span className={styles.inactiveFieldType}>
                                {selectedObservation.classification?.name}{' '}
                                {selectedObservation?.classification &&
                                    !selectedObservation?.classification?.active && (
                                        <InactiveIcon
                                            toolTipText={tcommon('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                        />
                                    )}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.potentialCause')}</span>
                            <span>{selectedObservation?.potentialCause}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.confirmedCause')}</span>
                            <span>{selectedObservation?.confirmedCause}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.owner')}</span>
                            <span className={styles.inactiveFieldType}>
                                {selectedObservation?.owner?.firstName} {selectedObservation?.owner?.lastName}
                                {selectedObservation?.owner && !selectedObservation?.owner?.active && (
                                    <InactiveIcon
                                        toolTipText={tcommon('common:inactiveIcon')}
                                        dataTestId="inactive-icon"
                                        tooltipPosition="bottom"
                                    />
                                )}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.responsibleFunction')}</span>
                            <span className={styles.inactiveFieldType}>
                                {selectedObservation?.responsibleFunction?.name}{' '}
                                {selectedObservation?.responsibleFunction &&
                                    !selectedObservation?.responsibleFunction?.active && (
                                        <InactiveIcon
                                            toolTipText={tcommon('common:inactiveIcon')}
                                            dataTestId="inactive-icon"
                                            tooltipPosition="bottom"
                                        />
                                    )}
                            </span>
                        </div>
                    </div>
                    <div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.comments')}</span>
                            <span>{selectedObservation?.comments}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.closedDate')}</span>
                            <span>{stringDateWithoutTimeFromUtc(selectedObservation.closedDate, 'DD-MMM-YYYY')}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.resolution')}</span>
                            <span>{selectedObservation?.resolution}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.recentActionDate')}</span>
                            <span>
                                {stringDateWithoutTimeFromUtc(selectedObservation.recentActionDate, 'DD-MMM-YYYY')}
                            </span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.numOpenActions')}</span>
                            <span>{selectedObservation?.numOpenActions}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.updatedBy')}</span>
                            <span>{selectedObservation?.updatedBy}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.updated')}</span>
                            <span>{stringDateWithoutTimeFromUtc(selectedObservation?.updated, 'DD-MMM-YYYY')}</span>
                        </div>
                        {selectedObservation.deletedDate && (
                            <>
                                <div>
                                    <span className="k-font-medium">{t('tabs.observation.reasonForDeletion')}</span>
                                    <span>{selectedObservation?.reasonForDeletion}</span>
                                </div>
                                <div>
                                    <span className="k-font-medium">{t('tabs.observation.deletedBy')}</span>
                                    <span>{selectedObservation?.deletedBy}</span>
                                </div>
                                <div>
                                    <span className="k-font-medium">{t('tabs.observation.deleted')}</span>
                                    <span>
                                        {stringDateWithoutTimeFromUtc(selectedObservation?.deletedDate, 'DD-MMM-YYYY')}
                                    </span>
                                </div>
                            </>
                        )}
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.createdBy')}</span>
                            <span>{selectedObservation?.createdBy}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.created')}</span>
                            <span>{stringDateWithoutTimeFromUtc(selectedObservation?.created, 'DD-MMM-YYYY')}</span>
                        </div>
                        <div>
                            <span className="k-font-medium">{t('tabs.observation.id')}</span>
                            <span>{selectedObservation?.recordId}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ObservationArea;
